import React, { FC } from "react"
import { Button, Select, Switch, Typography } from "antd"
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined } from "@ant-design/icons"

import { SurveyPayloadValue, SurveyQuestionDef, SurveyQuestionValue } from "../../store/types"

export const SurveyQuestionsEditor: FC<{
  value?: SurveyPayloadValue;
  onChange?: (v: SurveyPayloadValue) => void;
  allQuestions: SurveyQuestionDef[];
}> = ({ value, onChange, allQuestions }) => {
  const enabled = value?.enabled ?? false
  const questions: SurveyQuestionValue[] = value?.questions ?? []

  const emit = (patch: Partial<SurveyPayloadValue>) => onChange?.({ enabled, questions, ...patch })

  const move = (index: number, direction: -1 | 1) => {
    const next = [...questions]
    const target = index + direction

    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    emit({ questions: next })
  }

  const toggle = (index: number, field: "isRequired") => {
    const next = questions.map((q, i) => i === index ? { ...q, [field]: !q[field] } : q,
    )

    emit({ questions: next })
  }

  const remove = (index: number) => {
    emit({ questions: questions.filter((_, i) => i !== index) })
  }

  const addQuestion = (key: string) => {
    emit({ questions: [...questions, { key, isRequired: true }] })
  }

  const includedKeys = new Set(questions.map(q => q.key))
  const available = allQuestions.filter(q => !includedKeys.has(q.key))

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Switch
          checked={enabled}
          onChange={v => emit({ enabled: v })}
          size="small"
        />
        <Typography.Text style={{ fontSize: 12 }}>Survey enabled</Typography.Text>
      </div>

      {questions.map((q, i) => {
        const def = allQuestions.find(d => d.key === q.key)

        return (
          <div
            key={q.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#fafafa",
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              padding: "4px 8px",
            }}
          >
            <Typography.Text style={{ flex: 1, fontFamily: "monospace", fontSize: 12 }}>
              {i + 1}. {def?.label ?? q.key}
            </Typography.Text>

            <Switch
              checked={q.isRequired}
              onChange={() => toggle(i, "isRequired")}
              checkedChildren="required"
              unCheckedChildren="optional"
              size="small"
            />

            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={i === 0}
              onClick={() => move(i, -1)}
              aria-label="Move question up"
              title="Move up"
            />
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={i === questions.length - 1}
              onClick={() => move(i, 1)}
              aria-label="Move question down"
              title="Move down"
            />
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => remove(i)}
              aria-label="Remove question"
              title="Remove"
            />
          </div>
        )
      })}

      {available.length > 0 && (
        <Select
          placeholder="Add question"
          style={{ marginTop: 4 }}
          options={available.map(q => ({ value: q.key, label: q.label }))}
          // Use the first arg (the selected value) directly — matches the
          // pattern used in OrderedListEditor and avoids depending on the
          // option-object shape passed to onSelect, which can vary.
          onSelect={v => typeof v === "string" && addQuestion(v)}
          value={null}
        />
      )}
    </div>
  )
}
